import { useMountEffect, type AsyncStatus } from "@react-hookz/web";
import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";
import { useStore } from "zustand";

import { useClientSideLazyLoad } from "../../../../hooks/misc/useClientSideLazyLoad";
import { useGetPitchedContents } from "../../../../hooks/pitchedContent/useGetPitchedContents";
import type { AssociatedPitchedContent } from "../../../../store/editContent";
import { editContentStore } from "../../../../store/editContent";
import { isSuccess } from "../../../../utils/api.utils";
import { dateSort } from "../../../../utils/date.utils";
import { logInsightBrowseResults } from "../../../../utils/telemetry.utils";
import { withInsightsRenderer } from "../withInsightsRenderer";

import { PitchedContentCard } from "./PitchedContentsCard";
import { SharePitchedContentModal } from "./SharePitchedContentModal";

interface PitchedContentsProps {
    isLoaderInView: boolean;
    setAsyncStatus: Dispatch<SetStateAction<AsyncStatus>>;
    setTotalCount: Dispatch<SetStateAction<number>>;
}

export const PitchedContents: React.FunctionComponent<PitchedContentsProps> = (
    props: PitchedContentsProps,
) => {
    const { isLoaderInView, setAsyncStatus, setTotalCount } = props;
    const [state, action] = useGetPitchedContents({ autoFetch: true });

    const [lazyLoadedContents] = useClientSideLazyLoad(
        state.result ?? [],
        isLoaderInView ?? false,
    );

    useEffect(() => {
        if (isSuccess(state.status)) {
            setAsyncStatus(state.status);
            setTotalCount(state.result.length);
            logInsightBrowseResults(state.result.length);
        } else {
            setAsyncStatus(state.status);
        }
    }, [state.status, state.result]);

    const [setOnSubmitHandler] = useStore(editContentStore, (state) => [
        state.setOnSubmitHandler,
    ]);

    useMountEffect(() => {
        setOnSubmitHandler(() => void action.execute());
    });

    return (
        <>
            {(lazyLoadedContents as AssociatedPitchedContent[])
                .sort(dateSort("lastModifiedDate"))
                .map((content, index) => (
                    <PitchedContentCard
                        key={`pitched-${index}-${content.contentId}`}
                        content={content}
                        isEditable={true}
                        isSharable={true}
                    />
                ))}
            <SharePitchedContentModal />
        </>
    );
};

export const PitchedContentsViewer = withInsightsRenderer(PitchedContents);
